import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Wishlist.css'; // Your CSS file for styling
import UsersTopbar from '../UsersTopbar/UsersTopbar';
import api from '../../api';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import useBlockCheck from '../../hooks/useBlockCheck'; // Import the hook
import { FaStar, FaPlay, FaHeart } from 'react-icons/fa';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { getDisplayPrices } from '../../utils/priceUtils';


const Wishlist = () => {
  useBlockCheck('user'); // 🔥 Prevents blocked users from accessing this page
  const [wishlistItems, setWishlistItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState(''); // Add search query state
  const [selectedCourses, setSelectedCourses] = useState([]); // Track selected courses
  const { token } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const [wishlistRes, purchasedRes] = await Promise.all([
          axios.get(`${api.getCartAndWishlist}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          axios.get(`${api.getPurchasedCourses}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        const purchasedIds = purchasedRes.data.courses.map((course) => course._id);

        const filteredWishlist = wishlistRes.data.wishlist.filter(
          (item) => !purchasedIds.includes(item._id),
        );

        setWishlistItems(filteredWishlist);
      } catch (error) {
        console.error('Error fetching wishlist or purchased courses:', error);
      }
    };


    fetchWishlist();
  }, [token]);

  const handleRemoveFromWishlist = async (courseId) => {
    try {
      await axios.delete(`${api.addToWishlist}/${courseId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      setWishlistItems((prev) => prev.filter((item) => item._id !== courseId));
    } catch (error) {
      console.error('Error removing from wishlist:', error);
    }
  };

  const handleCheckboxChange = (courseId) => {
    setSelectedCourses((prev) =>
      prev.includes(courseId)
        ? prev.filter((id) => id !== courseId)
        : [...prev, courseId],
    );
  };

  const handleAddSelectedToCart = async () => {
    if (selectedCourses.length === 0) {
      toast.warning('Please select at least one course to add to the cart.');
      return;
    }

    try {
      // Remove selected courses from the wishlist and add them to the cart
      await Promise.all(
        selectedCourses.map(async (courseId) => {
          // Remove from wishlist
          await axios.delete(`${api.addToWishlist}/${courseId}`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          // Add to cart
          await axios.post(
            `${api.addToCart}`,
            { courseId },
            { headers: { Authorization: `Bearer ${token}` } },
          );
        }),
      );

      // Update local state
      setWishlistItems((prev) =>
        prev.filter((item) => !selectedCourses.includes(item._id)),
      );

      toast.success('Selected courses have been added to the cart.');
      setSelectedCourses([]); // Clear selected courses

      // Redirect to the cart page
      navigate('/cart');
    } catch (error) {
      console.error('Error adding selected courses to cart:', error);
      toast.error('Failed to add courses to cart. Please try again.');
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      // The search is already live/filtered, so we just need to prevent form submission
    }
  };

  // Filter items based on search query
  const filteredWishlist = wishlistItems.filter((item) =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  return (
    <div>
      <UsersTopbar />
      <ToastContainer />

      <div className="wishlist-container">
        <h1 className="wishlist-heading">My Wishlist</h1>

        {/* Search Bar */}
        <input
          type="text"
          placeholder="Search in wishlist..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onKeyPress={handleSearchKeyPress}
          className="wishlist-search-bar"
        />

        <div className="wishlist-items">
          {filteredWishlist.length === 0 ? (
            <div className="wishlist-empty-wrapper">
              <p className="wishlist-p">
                {searchQuery
                  ? 'No courses match your search.'
                  : 'Your wishlist is empty. Start exploring courses!'}
              </p>
              <button
                className="wishlist-browse-button"
                onClick={() =>
                  navigate('/category/all-courses/all-courses?search=')
                }
              >
                Browse Courses
              </button>
            </div>
          ) : (
            filteredWishlist.map((course) => (
              <div className="wishlist-course-card" key={course._id}>
                <div className="wishlist-course-card-image-wrapper"
                  onClick={() => navigate(`/courses/${course._id}`)}
                >
                  {course.image && (
                    <img
                      src={`${api.img + course.image}`}
                      alt={course.title}
                      className="wishlist-course-card-image"
                    />
                  )}
                  <div className="wishlist-play-button-overlay">
                    <FaPlay className="play-icon" />
                  </div>
                  <label
                    className="wishlist-checkbox-wrapper"
                    onClick={e => e.stopPropagation()} // Prevent navigation when clicking checkbox
                  >
                    <input
                      type="checkbox"
                      checked={selectedCourses.includes(course._id)}
                      onChange={() => handleCheckboxChange(course._id)}
                    />
                  </label>
                </div>
                <div>
                  <div className="wishlist-course-price-box">
                    {(() => {
                      const prices = getDisplayPrices(course);
                      return (
                        <>
                          <span className="wishlist-course-offer-price">
                            {prices.offerPrice === 0 ? (
                              <span className="wishlist-price-free">Free</span>
                            ) : (
                              `₹ ${prices.offerPrice}`
                            )}
                          </span>
                          <span className="wishlist-price-original">
                            ₹{prices.actualPrice}
                          </span>
                          {prices.offerPrice !== -1 && (
                            <span
                              className="wishlist-price-discount"
                              style={{ marginLeft: '8px' }}
                            >
                              {Math.round(
                                ((prices.actualPrice - prices.offerPrice) /
                                  prices.actualPrice) *
                                100,
                              )}
                              % Off
                            </span>
                          )}
                        </>
                      );
                    })()}
                  </div>

                  <div className="wishlist-course-card-box">
                    <h3 className="wishlist-course-card-h3">{course.title}</h3>
                  </div>
                  <div className="course-rating">
                    {Array.from({ length: 5 }).map((_, index) => (
                      <FaStar
                        key={index}
                        className={
                          index < Math.round(course.averageRating)
                            ? 'star filled'
                            : 'star'
                        }
                      />
                    ))}
                  </div>
                  <div className="wishlist-button-container">
                    <button
                      className="wishlist-remove-button"
                      onClick={() => handleRemoveFromWishlist(course._id)}
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {filteredWishlist.length > 0 && (
        <button
          className="wishlist-checkout-button"
          onClick={handleAddSelectedToCart}
          disabled={selectedCourses.length === 0}
        >
          Add to Cart
        </button>
      )}
    </div>
  );
};

export default Wishlist;